import './EditTeamMemberModal.scss';
import { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { InternalStakeholder } from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { NO_AUTOFILL_PROPS } from '@src/utils/form-utils';
import {
  EMPTY_TEAM_MEMBER_EDITS,
  isTeamMemberEditsValid,
  normalizeTeamMemberEdits,
  TeamMemberEdits,
  toTeamMemberEdits
} from '../edit-team-member-utils';

interface EditTeamMemberModalProps {
  stakeholder: InternalStakeholder | null;
  onHide: () => void;
  onSave: (edits: TeamMemberEdits) => void;
}

export const EditTeamMemberModal = ({ stakeholder, onHide, onSave }: EditTeamMemberModalProps) => {
  const [edits, setEdits] = useState<TeamMemberEdits>(EMPTY_TEAM_MEMBER_EDITS);

  // Re-seeding on the person means reopening on a different row never carries the previous row's
  // unsaved text across, and keeping the last edits while `stakeholder` goes null lets the modal
  // fade out with its content rather than emptying mid-transition.
  useEffect(() => {
    if (stakeholder) {
      setEdits(toTeamMemberEdits(stakeholder));
    }
  }, [stakeholder]);

  const { name, pronouns, title, roleOnAccount } = edits;

  const setField = (field: keyof TeamMemberEdits, value: string) =>
    setEdits({ ...edits, [field]: value });

  return (
    <Modal show={Boolean(stakeholder)} onHide={onHide} className='edit-team-member-modal' centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Team Member</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <EditField
          label='Team member'
          required
          value={name}
          onChange={value => setField('name', value)}
        />
        <EditField
          label='Pronouns'
          value={pronouns}
          onChange={value => setField('pronouns', value)}
        />
        <EditField label='Title' value={title} onChange={value => setField('title', value)} />
        <EditField
          label='Role on account'
          value={roleOnAccount}
          onChange={value => setField('roleOnAccount', value)}
        />
      </Modal.Body>

      <Modal.Footer className='onboarding-modal-footer'>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button
          disabled={!isTeamMemberEditsValid(edits)}
          onClick={() => onSave(normalizeTeamMemberEdits(edits))}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface EditFieldProps {
  label: string;
  value: string;
  required?: boolean;
  onChange: (value: string) => void;
}

const EditField = ({ label, value, required = false, onChange }: EditFieldProps) => (
  <Form.Group className='edit-field'>
    <Form.Label>
      {label}
      {required && <span className='required-marker'>*</span>}
    </Form.Label>
    <Form.Control
      {...NO_AUTOFILL_PROPS}
      type='text'
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </Form.Group>
);
